import { selectChainId, selectPositionConstants } from "context/SyntheticsStateContext/selectors/globalSelectors";
import {
  selectTradeboxCloseSizeUsd,
  selectTradeboxCollateralToken,
  selectTradeboxDecreasePositionAmounts,
  selectTradeboxFees,
  selectTradeboxFromToken,
  selectTradeboxFromTokenAmount,
  selectTradeboxIncreasePositionAmounts,
  selectTradeboxIsStakeOrUnstake,
  selectTradeboxIsWrapOrUnwrap,
  selectTradeboxLiquidity,
  selectTradeboxMarkPrice,
  selectTradeboxMarketInfo,
  selectTradeboxMaxLiquidityPath,
  selectTradeboxNextLeverageWithoutPnl,
  selectTradeboxNextPositionValues,
  selectTradeboxSelectedPosition,
  selectTradeboxStage,
  selectTradeboxSwapAmounts,
  selectTradeboxToToken,
  selectTradeboxToTokenAmount,
  selectTradeboxTradeFlags,
  selectTradeboxTradeRatios,
  selectTradeboxTriggerPrice,
  selectTradeboxTwapNumberOfParts,
} from "context/SyntheticsStateContext/selectors/tradeboxSelectors";
import { createSelector } from "context/SyntheticsStateContext/utils";
import {
  getRelevantPricesUpdatedAtForPosition,
  getRelevantPricesUpdatedAtForSwap,
} from "domain/synthetics/trade/utils/pricesUpdatedAt";
import {
  ValidationResult,
  getDecreaseError,
  getIncreaseError,
  getSwapError,
} from "domain/synthetics/trade/utils/validation";

export const selectTradeboxSwapTradeError = createSelector((q) => {
  const fromToken = q(selectTradeboxFromToken);
  const toToken = q(selectTradeboxToToken);
  const fromTokenAmount = q(selectTradeboxFromTokenAmount);
  const swapAmounts = q(selectTradeboxSwapAmounts);
  const toTokenAmount = q(selectTradeboxToTokenAmount);
  const { maxLiquidity: swapOutLiquidity } = q(selectTradeboxMaxLiquidityPath);
  const { isLimit, isTwap } = q(selectTradeboxTradeFlags);
  const isWrapOrUnwrap = q(selectTradeboxIsWrapOrUnwrap);
  const isStakeOrUnstake = q(selectTradeboxIsStakeOrUnstake);
  const { triggerRatio, markRatio } = q(selectTradeboxTradeRatios);
  const fees = q(selectTradeboxFees);
  const numberOfParts = q(selectTradeboxTwapNumberOfParts);
  const chainId = q(selectChainId);
  const relevantPricesUpdatedAt = getRelevantPricesUpdatedAtForSwap(
    fromToken?.prices.updatedAt,
    toToken?.prices.updatedAt
  );

  return getSwapError({
    fromToken,
    toToken,
    fromTokenAmount,
    fromUsd: swapAmounts?.usdIn,
    toTokenAmount,
    toUsd: swapAmounts?.usdOut,
    swapPathStats: swapAmounts?.swapStrategy.swapPathStats,
    swapLiquidity: swapOutLiquidity,
    externalSwapQuote: swapAmounts?.swapStrategy.externalSwapQuote,
    isLimit,
    isWrapOrUnwrap,
    isStakeOrUnstake,
    triggerRatio,
    markRatio,
    fees,
    isTwap,
    numberOfParts,
    chainId,
    pricesUpdatedAt: relevantPricesUpdatedAt,
  });
});

export const selectTradeboxIncreaseTradeError = createSelector((q) => {
  const marketInfo = q(selectTradeboxMarketInfo);
  const toToken = q(selectTradeboxToToken);
  const fromToken = q(selectTradeboxFromToken);
  const fromTokenAmount = q(selectTradeboxFromTokenAmount);
  const increaseAmounts = q(selectTradeboxIncreasePositionAmounts);
  const selectedPosition = q(selectTradeboxSelectedPosition);
  const fees = q(selectTradeboxFees);
  const { maxLiquidity: swapOutLiquidity } = q(selectTradeboxMaxLiquidityPath);
  const { minCollateralUsd, minPositionSizeUsd } = q(selectPositionConstants);
  const { longLiquidity, shortLiquidity } = q(selectTradeboxLiquidity);
  const { isLong, isLimit, isTwap } = q(selectTradeboxTradeFlags);
  const markPrice = q(selectTradeboxMarkPrice);
  const triggerPrice = q(selectTradeboxTriggerPrice);
  const nextPositionValues = q(selectTradeboxNextPositionValues);
  const nextLeverageWithoutPnl = q(selectTradeboxNextLeverageWithoutPnl);
  const numberOfParts = q(selectTradeboxTwapNumberOfParts);
  const chainId = q(selectChainId);
  const relevantPricesUpdatedAt = getRelevantPricesUpdatedAtForPosition(toToken?.prices.updatedAt);

  return getIncreaseError({
    marketInfo,
    indexToken: toToken,
    initialCollateralToken: fromToken,
    initialCollateralAmount: fromTokenAmount,
    initialCollateralUsd: increaseAmounts?.initialCollateralUsd,
    targetCollateralToken: q(selectTradeboxCollateralToken),
    collateralUsd: increaseAmounts?.collateralDeltaUsd,
    sizeDeltaUsd: increaseAmounts?.sizeDeltaUsd,
    existingPosition: selectedPosition,
    externalSwapQuote: increaseAmounts?.swapStrategy.externalSwapQuote,
    fees,
    swapPathStats: increaseAmounts?.swapStrategy.swapPathStats,
    collateralLiquidity: swapOutLiquidity,
    minCollateralUsd,
    longLiquidity,
    shortLiquidity,
    isLong,
    markPrice,
    triggerPrice,
    isLimit,
    nextPositionValues,
    nextLeverageWithoutPnl,
    thresholdType: increaseAmounts?.triggerThresholdType,
    numberOfParts,
    isTwap,
    minPositionSizeUsd,
    chainId,
    pricesUpdatedAt: relevantPricesUpdatedAt,
  });
});

export const selectTradeboxDecreaseTradeError = createSelector((q) => {
  const marketInfo = q(selectTradeboxMarketInfo);
  const closeSizeUsd = q(selectTradeboxCloseSizeUsd);
  const decreaseAmounts = q(selectTradeboxDecreasePositionAmounts);
  const selectedPosition = q(selectTradeboxSelectedPosition);
  const triggerPrice = q(selectTradeboxTriggerPrice);
  const markPrice = q(selectTradeboxMarkPrice);
  const nextPositionValues = q(selectTradeboxNextPositionValues);
  const { isLong, isTwap } = q(selectTradeboxTradeFlags);
  const { minCollateralUsd, minPositionSizeUsd } = q(selectPositionConstants);
  const stage = q(selectTradeboxStage);
  const numberOfParts = q(selectTradeboxTwapNumberOfParts);
  const relevantPricesUpdatedAt = getRelevantPricesUpdatedAtForPosition(
    (selectedPosition?.indexToken.prices as any)?.updatedAt
  );

  return getDecreaseError({
    marketInfo,
    inputSizeUsd: closeSizeUsd,
    sizeDeltaUsd: decreaseAmounts?.sizeDeltaUsd,
    triggerPrice,
    markPrice,
    existingPosition: selectedPosition,
    isContractAccount: false,
    receiveToken: selectedPosition?.collateralToken,
    nextPositionValues: nextPositionValues,
    isLong,
    isTrigger: true,
    minCollateralUsd,
    isNotEnoughReceiveTokenLiquidity: false,
    triggerThresholdType: stage !== "trade" ? decreaseAmounts?.triggerThresholdType : undefined,
    minPositionSizeUsd,
    isTwap,
    numberOfParts,
    pricesUpdatedAt: relevantPricesUpdatedAt,
  });
});

const DEFAULT_TRADE_ERROR: ValidationResult = [undefined];
export const selectTradeboxTradeTypeError = createSelector((q) => {
  const { isSwap, isIncrease, isTrigger } = q(selectTradeboxTradeFlags);
  let tradeError: ValidationResult = DEFAULT_TRADE_ERROR;

  if (isSwap) {
    tradeError = q(selectTradeboxSwapTradeError);
  } else if (isIncrease) {
    tradeError = q(selectTradeboxIncreaseTradeError);
  } else if (isTrigger) {
    tradeError = q(selectTradeboxDecreaseTradeError);
  }

  return tradeError;
});
